import { CheckCorrectTimeFormat } from '../../generic/DateTimeFunctions'

/**
 * Story 208 (MIC-2919): the optional machine time (`deviceTime`) of a task.
 *
 * Both values travel as `hh:mm:ss` strings. The machine time is informative only, but it can never be
 * longer than the duration of the task it belongs to (RN3): equal is allowed, and a task with no duration
 * is compared as a duration of zero — the same criterion Sun applies when it receives the workflow.
 */

const toSeconds = (time) => {
  const [hours, minutes, seconds] = time.split(':').map(Number)
  return hours * 3600 + minutes * 60 + seconds
}

// D8: two or more digits of hours, two of minutes and two of seconds, 00-59. A subset of what Sun
// accepts (Utils.checkIfIsTimeString), so the editor never lets through what the save would reject.
const STRICT_TIME = /^\d{2,}:[0-5]\d:[0-5]\d$/

// "Well formed" means the same thing for the bound and for the format check of both columns (S52).
const isWellFormed = (time) => STRICT_TIME.test(time)

/**
 * D8 (S49/S50): the format check of the two time columns of the task table, the duration and the machine
 * time. It speaks with the messages of the shared check (`CheckCorrectTimeFormat`, which the other
 * tables keep using as it is) and adds what that one lets through: "0:0:0", "00:00:5", "00:00:1.5"…
 * @param {string} [time] The typed value; empty or absent is not this check's to judge.
 * @param {function(string): string} t_ The translation function.
 * @returns {string} '' when the value is fine, the translated message otherwise.
 */
export const timeFormatError = (time, t_) => {
  if (!time) {
    return ''
  }
  const sharedError = CheckCorrectTimeFormat(time, t_)
  if (sharedError) {
    return sharedError
  }
  return STRICT_TIME.test(time) ? '' : t_('Time must have hh:mm:ss structure')
}

/**
 * @param {string} [deviceTime] The machine time, `hh:mm:ss`; empty or absent means "no data".
 * @param {string} [theoreticalTime] The duration of the same task, `hh:mm:ss`.
 * @returns {boolean} `true` only when both are well formed and the machine time is strictly longer.
 * A malformed value is not this rule's to judge: the format check of its own column reports it.
 */
export const machineTimeExceedsDuration = (deviceTime, theoreticalTime) => {
  if (!deviceTime || !isWellFormed(deviceTime)) {
    return false
  }
  if (!theoreticalTime) {
    return toSeconds(deviceTime) > 0
  }
  if (!isWellFormed(theoreticalTime)) {
    return false
  }
  return toSeconds(deviceTime) > toSeconds(theoreticalTime)
}
